import { Route } from '@angular/router';
import { InitialDataResolver } from 'app/app.resolvers';
import {
    AGENTE_ANALYTICS_PUESTOS,
    AGENTE_CHAT_PUESTOS,
    AGENTE_GESTION_PUESTOS,
    COMPETENCIA_PUESTOS,
    GARANTIA_PUESTOS,
    OJOS3D_PUESTOS,
    TRYON_PUESTOS,
} from 'app/core/auth/auth.permissions';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { OfflineAuthGuard } from 'app/core/auth/guards/offline-auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { PuestosGuard, PuestosMatchGuard } from 'app/core/auth/guards/puestos.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/inicio-sucursal'
    { path: '', pathMatch: 'full', redirectTo: 'inicio-sucursal' },

    // Redirect signed-in user to the '/inicio-sucursal'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'inicio-sucursal',
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.module'
                    ).then((m) => m.AuthConfirmationRequiredModule),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.module'
                    ).then((m) => m.AuthForgotPasswordModule),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.module'
                    ).then((m) => m.AuthResetPasswordModule),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.module').then(
                        (m) => m.AuthSignInModule
                    ),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.module').then(
                        (m) => m.AuthSignUpModule
                    ),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.module').then(
                        (m) => m.AuthSignOutModule
                    ),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.module'
                    ).then((m) => m.AuthUnlockSessionModule),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.module').then(
                        (m) => m.LandingHomeModule
                    ),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            // Dashboards
            {
                path: 'dashboards',
                children: [
                    {
                        path: 'projec',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/dashboards/project/project.module'
                            ).then((m) => m.ProjectModule),
                    },

                    {
                        path: 'finance',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/dashboards/finance/finance.module'
                            ).then((m) => m.FinanceModule),
                    },
                    {
                        path: 'crypto',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/dashboards/crypto/crypto.module'
                            ).then((m) => m.CryptoModule),
                    },
                ],
            },
            // Caja
            {
                path: 'caja',
                children: [
                    {
                        path: 'caja',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/caja/caja/caja.module'
                            ).then((m) => m.CajaModule),
                    },
                ],
            },
            // Workflow de Atención (Recepción → Consultorio → Caja)
            {
                path: 'atencion',
                canActivate: [PuestosGuard],
                canMatch: [PuestosMatchGuard],
                data: { puestos: ['RECEPCIONISTA', 'VENDEDOR', 'OPTOMETRISTA', 'GERENTE', 'ADMINISTRADOR'] },
                loadChildren: () =>
                    import(
                        'app/modules/admin/workflow-atencion/workflow-atencion.module'
                    ).then((m) => m.WorkflowAtencionFrontModule),
            },
            // Cita
            {
                path: 'cita',
                children: [
                    {
                        path: 'cita',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/cita/cita/cita.module'
                            ).then((m) => m.CitaModule),
                    },
                ],
            },
            // Receta
            {
                path: 'receta',
                children: [
                    {
                        path: 'receta',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/receta/receta/receta.module'
                            ).then((m) => m.RecetaModule),
                    },
                ],
            },
            // Venta
            {
                path: 'venta',
                children: [
                    {
                        path: 'venta',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/venta/venta/venta.module'
                            ).then((m) => m.VentaModule),
                    },
                    {
                        path: 'ventas-jornada',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/venta/ventas-jornada/ventas-jornada.module'
                            ).then((m) => m.VentasJornadaModule),
                    },
                    {
                        // Módulo Cotización v3.2, 2026-10-08
                        path: 'cotizacion',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/cotizacion/cotizacion.module'
                            ).then((m) => m.CotizacionModule),
                    },
                    {
                        path: 'garantia',
                        canActivate: [PuestosGuard],
                        canMatch: [PuestosMatchGuard],
                        data: { puestos: GARANTIA_PUESTOS },
                        loadChildren: () =>
                            import(
                                'app/modules/admin/venta/garantia/garantia.module'
                            ).then((m) => m.GarantiaModule),
                    },
                ],
            },
            // Solicitudes
            {
                path: 'solicitudes',
                children: [
                    {
                        path: 'cancelar-ventas',
                        loadComponent: () => import('app/modules/admin/venta/abono/seguimiento/solicitudes-cancelar-ventas.component').then(m => m.SolicitudesCancelarVentasComponent)
                    },
                    {
                        path: 'cancelar-abonos',
                        loadComponent: () => import('app/modules/admin/venta/abono/seguimiento/seguimiento-cancelaciones.component').then(m => m.SeguimientoCancelacionesComponent)
                    },
                    {
                        path: 'aprobar-abonos',
                        loadComponent: () => import('app/modules/admin/venta/abono/seguimiento/aprobar-abonos.component').then(m => m.AprobarAbonosComponent)
                    }
                ]
            },
            // contabilidad
            {
                path: 'contabilidad',
                children: [
                    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
                    {
                        path: 'dashboard',
                        loadComponent: () =>
                            import(
                                'app/modules/admin/contabilidad/dashboard/contabilidad-dashboard.component'
                            ).then((m) => m.ContabilidadDashboardComponent),
                    },
                    {
                        path: 'gasto',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/contabilidad/gasto/gasto.module'
                            ).then((m) => m.GastoModule),
                    },
                    {
                        path: 'factura',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/contabilidad/factura/factura.module'
                            ).then((m) => m.FacturaModule),
                    },
                    {
                        path: 'comision',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/contabilidad/comision/comision.module'
                            ).then((m) => m.ComisionModule),
                    },
                    {
                        path: 'costos-venta',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/contabilidad/costos-venta/costos-venta.module'
                            ).then((m) => m.CostosVentaModule),
                    },
                    {
                        path: 'configuracion',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/configuracion/configuracion.module'
                            ).then((m) => m.ConfiguracionModule),
                    },
                    {
                        path: 'orden-compra',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/contabilidad/orden-compra/orden-compra.module'
                            ).then((m) => m.OrdenCompraModule),
                    },
                    {
                        path: 'corte-mensual',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/contabilidad/corte-mensual/corte-mensual.module'
                            ).then((m) => m.CorteMensualModule),
                    },
                    {
                        path: 'cuentas-por-cobrar',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/cuentas-por-cobrar/cuentas-por-cobrar.module'
                            ).then((m) => m.CuentasPorCobrarModule),
                    },
                    {
                        path: 'nomina',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/contabilidad/nomina/nomina.module'
                            ).then((m) => m.NominaModule),
                    },
                    {
                        path: 'balance',
                        pathMatch: 'full',
                        redirectTo: '/inteligencia/balance',
                    },
                ],
            },
            // inventarios
            {
                path: 'inventarios',
                children: [
                    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
                    {
                        path: 'dashboard',
                        loadComponent: () =>
                            import(
                                'app/modules/admin/inventarios/dashboard/inventarios-dashboard.component'
                            ).then((m) => m.InventariosDashboardComponent),
                    },
                    {
                        path: 'armazones',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/inventarios/armazones/armazon.module'
                            ).then((m) => m.ArmazonModule),
                    }
                ],
            },
            // Probador de armazones (virtual try-on en tiempo real con la camara).
            {
                path: 'probador',
                canActivate: [PuestosGuard],
                canMatch: [PuestosMatchGuard],
                data: { puestos: TRYON_PUESTOS },
                loadChildren: () =>
                    import(
                        'app/modules/admin/apps/tryon/tryon.module'
                    ).then((m) => m.TryonModule),
            },
            // Modulo "Ojo 3D": explicacion 3D de enfermedades oculares para
            // explicar al paciente en la tablet del consultorio.
            {
                path: 'ojos-3d',
                canActivate: [PuestosGuard],
                canMatch: [PuestosMatchGuard],
                data: { puestos: OJOS3D_PUESTOS },
                loadChildren: () =>
                    import(
                        'app/modules/admin/apps/ojos-3d/ojos-3d.module'
                    ).then(m => m.Ojos3dModule),
            },
            // Investigacion de competencia con IA.
            {
                path: 'competencia',
                canActivate: [PuestosGuard],
                canMatch: [PuestosMatchGuard],
                data: { puestos: COMPETENCIA_PUESTOS },
                loadChildren: () =>
                    import(
                        'app/modules/admin/apps/competencia/competencia.module'
                    ).then((m) => m.CompetenciaModule),
            },
            {
                path: 'configuracion',
                loadChildren: () =>
                    import(
                        'app/modules/admin/configuracion/configuracion.module'
                    ).then((m) => m.ConfiguracionModule),
            },
            // catalogos
            {
                path: 'catalogos',
                loadComponent: () =>
                    import(
                        'app/modules/admin/catalogos/dashboard/catalogos-dashboard.component'
                    ).then((m) => m.CatalogosDashboardComponent),
            },
            // proveedor
            {
                path: 'inicio-sucursal',
                loadChildren: () =>
                    import(
                        'app/modules/admin/inicio-sucursal/inicio-sucursal.module'
                    ).then((m) => m.InicioSucursalModule),
            },
            {
                path: 'proveedor',
                children: [
                    {
                        path: 'laboratorio',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/proveedor/laboratorio/laboratorio.module'
                            ).then((m) => m.LaboratorioModule),
                    }
                ],
            },
            // Apps
            {
                path: 'apps',
                children: [
                    {
                        path: 'auditoria',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/auditoria-finanzas/auditoria-finanzas.routes'
                            ).then((m) => m.auditoriaFinanzasRoutes),
                    },
                    {
                        path: 'cliente',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/cliente/cliente.module'
                            ).then((m) => m.ClienteModule),
                    },
                    {
                        path: 'chat',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/chat/chat.module'
                            ).then((m) => m.ChatModule),
                    },
                    {
                        path: 'agente-chat',
                        canActivate: [PuestosGuard],
                        canMatch: [PuestosMatchGuard],
                        data: { puestos: AGENTE_CHAT_PUESTOS },
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/agente-chat/agente-chat.module'
                            ).then((m) => m.AgenteChatModule),
                    },
                    {
                        path: 'analisis-grounded',
                        canActivate: [PuestosGuard],
                        canMatch: [PuestosMatchGuard],
                        data: { puestos: AGENTE_CHAT_PUESTOS },
                        loadComponent: () =>
                            import(
                                'app/modules/admin/apps/agente-chat/components/analisis-grounded/analisis-grounded.component'
                            ).then((m) => m.AnalisysGroundedComponent),
                    },
                    {
                        path: 'empleado',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/empleado/empleado.module'
                            ).then((m) => m.EmpleadoModule),
                    },
                    {
                        path: 'proveedor',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/proveedor/proveedor.module'
                            ).then((m) => m.ProveedorModule),
                    },
                    {
                        path: 'familia',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/familia/familia.module'
                            ).then((m) => m.FamiliaModule),
                    },
                    {
                        path: 'paciente',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/paciente/paciente.module'
                            ).then((m) => m.PacienteModule),
                    },
                    {
                        path: 'producto',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/producto/producto.module'
                            ).then((m) => m.ProductoModule),
                    },
                    {
                        path: 'insumo',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/insumo/insumo.module'
                            ).then((m) => m.InsumoModule),
                    },
                    {
                        path: 'convenio',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/convenio/convenio.module'
                            ).then((m) => m.ConvenioModule),
                    }, {
                        path: 'beneficio',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/beneficio/beneficio.module'
                            ).then((m) => m.BeneficioModule),
                    },
                    {
                        path: 'cupon',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/cupon/cupon.module'
                            ).then((m) => m.CuponModule),
                    },
                    {
                        path: 'kanban',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/kanban/kanban.module'
                            ).then((m) => m.KanbanModule),
                    },

                    {
                        path: 'terminal',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/terminal/terminal.module'
                            ).then((m) => m.TerminalModule),
                    },
                    {
                        path: 'intermediario-pago',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/intermediario-pago/intermediario-pago.module'
                            ).then((m) => m.IntermediarioPagoModule),
                    },
                    {
                        path: 'cuenta-bancaria',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/cuenta-bancaria/cuenta-bancaria.module'
                            ).then((m) => m.CuentaBancariaModule),
                    },
                    {
                        path: 'corte-mensual',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/corte-mensual/corte-mensual.module'
                            ).then((m) => m.CorteMensualModule),
                    },
                    {
                        path: 'jornadas',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/jornadas/jornadas.routing'
                            ).then((m) => m.jornadasRoutes),
                    },
                    {
                        path: 'salida-jornada',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/salida-jornada/salida-jornada.routing'
                            ).then((m) => m.salidaJornadaRoutes),
                    },
                    {
                        path: 'marca',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/marca/marca.module'
                            ).then((m) => m.MarcaModule),
                    },
                    {
                        path: 'incidencia',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/incidencia/incidencia.module'
                            ).then((m) => m.IncidenciaModule),
                    },
                    {
                        // Scrum module (S8-S10) — wishlist / issues / sprints
                        path: 'scrum',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/scrum/scrum.module'
                            ).then((m) => m.ScrumModule),
                    },
                ],
            },
            // Pages
            {
                path: 'pages',
                children: [
                    // Activities
                    {
                        path: 'activities',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/pages/activities/activities.module'
                            ).then((m) => m.ActivitiesModule),
                    },
                    // Coming Soon
                    {
                        path: 'coming-soon',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/pages/coming-soon/coming-soon.module'
                            ).then((m) => m.ComingSoonModule),
                    },

                    // Error
                    {
                        path: 'error',
                        children: [
                            {
                                path: '404',
                                loadChildren: () =>
                                    import(
                                        'app/modules/admin/pages/error/error-404/error-404.module'
                                    ).then((m) => m.Error404Module),
                            },
                            {
                                path: '500',
                                loadChildren: () =>
                                    import(
                                        'app/modules/admin/pages/error/error-500/error-500.module'
                                    ).then((m) => m.Error500Module),
                            },
                        ],
                    },

                    // Invoice
                    {
                        path: 'invoice',
                        children: [
                            {
                                path: 'printable',
                                children: [
                                    {
                                        path: 'compact',
                                        loadChildren: () =>
                                            import(
                                                'app/modules/admin/pages/invoice/printable/compact/compact.module'
                                            ).then((m) => m.CompactModule),
                                    },
                                    {
                                        path: 'modern',
                                        loadChildren: () =>
                                            import(
                                                'app/modules/admin/pages/invoice/printable/modern/modern.module'
                                            ).then((m) => m.ModernModule),
                                    },
                                ],
                            },
                        ],
                    },

                    // Maintenance
                    {
                        path: 'maintenance',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/pages/maintenance/maintenance.module'
                            ).then((m) => m.MaintenanceModule),
                    },

                    // Pricing
                    {
                        path: 'pricing',
                        children: [
                            {
                                path: 'modern',
                                loadChildren: () =>
                                    import(
                                        'app/modules/admin/pages/pricing/modern/modern.module'
                                    ).then((m) => m.PricingModernModule),
                            },
                            {
                                path: 'simple',
                                loadChildren: () =>
                                    import(
                                        'app/modules/admin/pages/pricing/simple/simple.module'
                                    ).then((m) => m.PricingSimpleModule),
                            },
                            {
                                path: 'single',
                                loadChildren: () =>
                                    import(
                                        'app/modules/admin/pages/pricing/single/single.module'
                                    ).then((m) => m.PricingSingleModule),
                            },
                            {
                                path: 'table',
                                loadChildren: () =>
                                    import(
                                        'app/modules/admin/pages/pricing/table/table.module'
                                    ).then((m) => m.PricingTableModule),
                            },
                        ],
                    },

                    // Profile
                    {
                        path: 'profile',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/pages/profile/profile.module'
                            ).then((m) => m.ProfileModule),
                    },

                    // Settings
                    {
                        path: 'settings',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/pages/settings/settings.module'
                            ).then((m) => m.SettingsModule),
                    },
                ],
            },

            // User Interface
            {
                path: 'ui',
                children: [
                    // Material Components
                    {
                        path: 'material-components',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/material-components/material-components.module'
                            ).then((m) => m.MaterialComponentsModule),
                    },

                    // Fuse Components
                    {
                        path: 'fuse-components',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/fuse-components/fuse-components.module'
                            ).then((m) => m.FuseComponentsModule),
                    },

                    // Other Components
                    {
                        path: 'other-components',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/other-components/other-components.module'
                            ).then((m) => m.OtherComponentsModule),
                    },

                    // TailwindCSS
                    {
                        path: 'tailwindcss',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/tailwindcss/tailwindcss.module'
                            ).then((m) => m.TailwindCSSModule),
                    },

                    // Advanced Search
                    {
                        path: 'advanced-search',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/advanced-search/advanced-search.module'
                            ).then((m) => m.AdvancedSearchModule),
                    },

                    // Animations
                    {
                        path: 'animations',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/animations/animations.module'
                            ).then((m) => m.AnimationsModule),
                    },

                    // Cards
                    {
                        path: 'cards',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/cards/cards.module'
                            ).then((m) => m.CardsModule),
                    },

                    // Colors
                    {
                        path: 'colors',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/colors/colors.module'
                            ).then((m) => m.ColorsModule),
                    },

                    // Confirmation Dialog
                    {
                        path: 'confirmation-dialog',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/confirmation-dialog/confirmation-dialog.module'
                            ).then((m) => m.ConfirmationDialogModule),
                    },

                    // Datatable
                    {
                        path: 'datatable',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/datatable/datatable.module'
                            ).then((m) => m.DatatableModule),
                    },

                    // Forms
                    {
                        path: 'forms',
                        children: [
                            {
                                path: 'fields',
                                loadChildren: () =>
                                    import(
                                        'app/modules/admin/ui/forms/fields/fields.module'
                                    ).then((m) => m.FormsFieldsModule),
                            },
                            {
                                path: 'layouts',
                                loadChildren: () =>
                                    import(
                                        'app/modules/admin/ui/forms/layouts/layouts.module'
                                    ).then((m) => m.FormsLayoutsModule),
                            },
                            {
                                path: 'wizards',
                                loadChildren: () =>
                                    import(
                                        'app/modules/admin/ui/forms/wizards/wizards.module'
                                    ).then((m) => m.FormsWizardsModule),
                            },
                        ],
                    },

                    // Icons
                    {
                        path: 'icons',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/icons/icons.module'
                            ).then((m) => m.IconsModule),
                    },

                    // Page Layouts
                    {
                        path: 'page-layouts',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/page-layouts/page-layouts.module'
                            ).then((m) => m.PageLayoutsModule),
                    },

                    // Typography
                    {
                        path: 'typography',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/ui/typography/typography.module'
                            ).then((m) => m.TypographyModule),
                    },
                ],
            },

            // Documentation
            {
                path: 'docs',
                children: [
                    // Changelog
                    {
                        path: 'changelog',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/docs/changelog/changelog.module'
                            ).then((m) => m.ChangelogModule),
                    },

                    // Guides
                    {
                        path: 'guides',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/docs/guides/guides.module'
                            ).then((m) => m.GuidesModule),
                    },
                ],
            },

            // Metas & OKRs
            {
                path: 'metas',
                canActivate: [PuestosGuard],
                canMatch: [PuestosMatchGuard],
                data: { puestos: AGENTE_GESTION_PUESTOS },
                loadChildren: () =>
                    import('app/modules/admin/metas/metas.module').then(
                        (m) => m.MetasModule,
                    ),
            },

            // Agente administrativo
            {
                path: 'agente',
                canActivate: [PuestosGuard],
                canMatch: [PuestosMatchGuard],
                data: { puestos: AGENTE_GESTION_PUESTOS },
                loadChildren: () =>
                    import('app/modules/admin/agente/agente.module').then(
                        (m) => m.AgenteModule,
                    ),
            },
            {
                path: 'agente-chat',
                canActivate: [PuestosGuard],
                canMatch: [PuestosMatchGuard],
                data: { puestos: AGENTE_CHAT_PUESTOS },
                loadChildren: () =>
                    import(
                        'app/modules/admin/apps/agente-chat/agente-chat.module'
                    ).then((m) => m.AgenteChatModule),
            },
            {
                path: 'inteligencia',
                canActivate: [PuestosGuard],
                canMatch: [PuestosMatchGuard],
                data: { puestos: AGENTE_ANALYTICS_PUESTOS },
                loadChildren: () =>
                    import(
                        'app/modules/admin/inteligencia/inteligencia.module'
                    ).then((m) => m.InteligenciaModule),
            },
            {
                path: 'reportes',
                canActivate: [PuestosGuard],
                canMatch: [PuestosMatchGuard],
                data: { puestos: AGENTE_GESTION_PUESTOS },
                loadChildren: () =>
                    import('app/modules/admin/reportes/reportes.module').then(
                        (m) => m.ReportesModule,
                    ),
            },

            // RH - Recursos Humanos
            {
                path: 'rh',
                loadChildren: () =>
                    import('app/modules/admin/rh/rh.module').then(
                        (m) => m.RhModule,
                    ),
            },
        ],
    },

    // -----------------------------------------------------------------
    // Jornada - TPV offline.
    //
    // Vive FUERA del bloque admin a proposito: ese bloque tiene
    // `canActivateChild: [AuthGuard]` en el padre, y los guards de padre
    // corren ANTES que los del hijo, asi que cualquier ruta anidada ahi
    // pasaria por la revalidacion contra el servidor y el TPV no abriria
    // sin conexion.
    //
    // Aqui el guard por defecto es `OfflineAuthGuard` (solo mira la
    // expiracion del token local, sin red). Las rutas que SI necesitan
    // servidor —sincronizar— agregan `AuthGuard` encima.
    // -----------------------------------------------------------------
    {
        path: '',
        canActivate: [OfflineAuthGuard],
        canActivateChild: [OfflineAuthGuard],
        component: LayoutComponent,
        data: { layout: 'empty' },
        children: [
            {
                path: 'jornada',
                children: [
                    {
                        // Sincronizar pega al servidor: requiere auth normal.
                        path: 'sincronizar',
                        canActivate: [AuthGuard],
                        canActivateChild: [AuthGuard],
                        loadComponent: () =>
                            import(
                                'app/modules/admin/venta/venta/tpv-offline/sincronizar/sincronizar.component'
                            ).then(m => m.SincronizarComponent),
                    },
                    {
                        // Selector de jornada como pantalla propia: siempre
                        // visible, para corregir una eleccion previa.
                        path: 'elegir-jornada',
                        loadComponent: () =>
                            import(
                                'app/modules/admin/venta/venta/tpv-offline/elegir-jornada/elegir-jornada.component'
                            ).then(m => m.ElegirJornadaComponent),
                    },
                    {
                        // TPV de venta: hereda OfflineAuthGuard del padre.
                        path: 'tpv-offline',
                        loadComponent: () =>
                            import(
                                'app/modules/admin/venta/venta/tpv-offline/tpv-offline.component'
                            ).then(m => m.TpvOfflineComponent),
                    },
                    {
                        // Recetas capturadas en campo: ver, corregir y descartar.
                        // Es el destino del aviso de la pantalla "Subir" cuando
                        // una receta viene vacía o con un eje inválido.
                        path: 'tpv-offline/recetas',
                        loadComponent: () =>
                            import(
                                'app/modules/admin/venta/venta/tpv-offline/screens/recetas/recetas.component'
                            ).then(m => m.RecetasComponent),
                    },
                    {
                        // Pantalla Subir. Cierra el circulo de la jornada.
                        path: 'subir',
                        loadComponent: () =>
                            import(
                                'app/modules/admin/venta/venta/tpv-offline/subir/subir.component'
                            ).then(m => m.SubirComponent),
                    },
                    {
                        // Catalogo de productos pendientes de catalogar.
                        path: 'subir/catalogo',
                        loadComponent: () =>
                            import(
                                'app/modules/admin/venta/venta/tpv-offline/catalogo-pendientes/catalogo-pendientes.component'
                            ).then(m => m.CatalogoPendientesComponent),
                    },
                ],
            },
        ],
    },

    // 404 & Catch all
    {
        path: '404-not-found',
        pathMatch: 'full',
        loadChildren: () =>
            import(
                'app/modules/admin/pages/error/error-404/error-404.module'
            ).then((m) => m.Error404Module),
    },
    { path: '**', redirectTo: '404-not-found' },
];
